import type {
  AggregatedReactionDto,
  CommentResponseDto,
} from '@splitfiesta/shared';

import { CommentSection } from './CommentSection';
import { ReactionBar } from './ReactionBar';

interface SocialInteractionsProps {
  // Reactions
  reactions?: AggregatedReactionDto[];
  onAddReaction: (emoji: string) => void | Promise<void>;
  onToggleReaction: (
    emoji: string,
    hasReacted: boolean
  ) => void | Promise<void>;
  loadingReaction?: boolean;

  // Comments
  comments?: CommentResponseDto[];
  currentUserId?: string;
  currentUserName?: string;
  currentUserProfileImage?: string;
  onSubmitComment: (text: string) => Promise<void>;
  onEditComment: (commentId: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  commentPlaceholder?: string;

  // Load more comments
  hasMoreComments?: boolean;
  onLoadMoreComments?: () => void | Promise<void>;
  loadingMoreComments?: boolean;

  // Layout options
  variant?: 'default' | 'compact';
  showDivider?: boolean;
  showSupporterBadges?: boolean;
}

/**
 * Unified component for social interactions (reactions and comments)
 * Used in both expense detail pages and activity feed
 */
export function SocialInteractions({
  // Reactions
  reactions = [],
  onAddReaction,
  onToggleReaction,
  loadingReaction = false,

  // Comments
  comments = [],
  currentUserId,
  currentUserName = '',
  currentUserProfileImage,
  onSubmitComment,
  onEditComment,
  onDeleteComment,
  commentPlaceholder,

  // Load more comments
  hasMoreComments = false,
  onLoadMoreComments,
  loadingMoreComments = false,

  // Layout
  variant = 'default',
  showDivider = true,
  showSupporterBadges = false,
}: SocialInteractionsProps) {
  const isCompact = variant === 'compact';

  return (
    <div
      className={`social-interactions ${isCompact ? 'space-y-3' : 'space-y-4'}`}
      data-testid="social-interactions"
    >
      {/* Reactions Section - aligned with comment input (avatar width 32px + gap 8px = 40px) */}
      <div className={`${isCompact ? '' : 'pb-3'} pl-10`}>
        <ReactionBar
          reactions={reactions}
          onAddReaction={onAddReaction}
          onToggleReaction={onToggleReaction}
          loading={loadingReaction}
          currentUserId={currentUserId}
        />
      </div>

      {/* Divider */}
      {showDivider && reactions.length > 0 && comments.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700" />
      )}

      {/* Comments Section */}
      <CommentSection
        comments={comments}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserProfileImage={currentUserProfileImage}
        onSubmitComment={onSubmitComment}
        onEditComment={onEditComment}
        onDeleteComment={onDeleteComment}
        placeholder={commentPlaceholder}
        hasMoreComments={hasMoreComments}
        onLoadMoreComments={onLoadMoreComments}
        loadingMoreComments={loadingMoreComments}
        showSupporterBadges={showSupporterBadges}
      />
    </div>
  );
}
